import React, { useEffect, useState } from 'react';
import { RefreshCw, ChevronDown, ChevronUp, CheckCircle2, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';

const SEVERIDAD_COLORS = {
  critico: { bg: '#FFEBEE', color: '#C62828', label: 'Crítico' },
  aviso: { bg: '#FFF3E0', color: '#E65100', label: 'Aviso' },
};

// Qué significa cada código, en lenguaje del dueño (no del programador).
function explicarStatus(codigo) {
  if (codigo === 0) return 'Sin respuesta (se cayó la conexión o el navegador la bloqueó)';
  if (codigo === 413) return 'Archivo demasiado pesado para el servidor';
  if (codigo === 429) return 'Demasiados intentos seguidos (límite del servidor)';
  if (codigo === 502 || codigo === 504) return 'El servidor no respondió a tiempo';
  if (codigo >= 500) return 'Falla interna del servidor';
  if (codigo === 400) return 'Datos rechazados por validación';
  if (codigo === 403) return 'Sin permiso';
  if (codigo === 404) return 'No encontrado';
  return 'Petición rechazada';
}

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-EC', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function ErroresClientesPanel({ onCambio }) {
  const [errores, setErrores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useState('pendientes'); // pendientes | resueltos
  const [filtroSeveridad, setFiltroSeveridad] = useState('');
  const [soloClientes, setSoloClientes] = useState(true);
  const [expandido, setExpandido] = useState(null);
  const [notasTemp, setNotasTemp] = useState({});
  const [seleccionados, setSeleccionados] = useState(new Set());

  const cargar = () => {
    setCargando(true);
    setSeleccionados(new Set());
    const params = { resuelto: vista === 'resueltos' };
    if (filtroSeveridad) params.severidad = filtroSeveridad;
    if (soloClientes) params.rol = 'cliente';
    api.get('/errores-usuarios/', { params })
      .then((res) => setErrores(res.data))
      .catch((err) => toast.error(mensajeErrorApi(err, 'No se pudieron cargar los errores.')))
      .finally(() => setCargando(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [vista, filtroSeveridad, soloClientes]);

  const guardar = async (id, cambios, mensaje) => {
    try {
      await api.patch(`/errores-usuarios/${id}/`, cambios);
      toast.success(mensaje);
      cargar();
      if (onCambio) onCambio();
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo guardar.'));
    }
  };

  const resolverSeleccionados = async () => {
    try {
      await api.post('/errores-usuarios/resolver-lote/', { ids: Array.from(seleccionados) });
      toast.success(`${seleccionados.size} error(es) marcados como resueltos.`);
      cargar();
      if (onCambio) onCambio();
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudieron marcar.'));
    }
  };

  const toggle = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const criticos = errores.filter((e) => e.severidad === 'critico').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-2)', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Errores que ven los usuarios</h2>
        <div className="toolbar">
          {seleccionados.size > 0 && (
            <button className="btn-secondary btn-sm" onClick={resolverSeleccionados} style={{ whiteSpace: 'nowrap' }}>
              <CheckCircle2 size={14} /> Marcar resueltos ({seleccionados.size})
            </button>
          )}
          <select value={vista} onChange={(e) => setVista(e.target.value)} style={{ minWidth: '140px' }}>
            <option value="pendientes">Pendientes</option>
            <option value="resueltos">Resueltos</option>
          </select>
          <select value={filtroSeveridad} onChange={(e) => setFiltroSeveridad(e.target.value)} style={{ minWidth: '140px' }}>
            <option value="">Todas las severidades</option>
            <option value="critico">Solo críticos</option>
            <option value="aviso">Solo avisos</option>
          </select>
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: 0, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={soloClientes} onChange={(e) => setSoloClientes(e.target.checked)} />
            Solo clientes
          </label>
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>
      <p className="text-sm text-secondary" style={{ marginBottom: 'var(--spacing-4)' }}>
        Se registran solos. <strong>Crítico</strong> = el usuario quedó bloqueado (archivo muy pesado, servidor caído, sin respuesta) y te llega un correo;
        {' '}<strong>Aviso</strong> = el sistema le explicó qué corregir (campo faltante, permiso). El mismo error repetido se agrupa en una sola fila.
        {vista === 'pendientes' && !cargando && ` Pendientes: ${errores.length} (${criticos} críticos).`}
      </p>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }} />
              <th>Última vez</th>
              <th>Gravedad</th>
              <th>Quién</th>
              <th>Qué intentaba</th>
              <th>Qué pasó</th>
              <th>Veces</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={8} className="text-secondary">Cargando...</td></tr>
            ) : errores.length === 0 ? (
              <tr><td colSpan={8} className="text-secondary">{vista === 'pendientes' ? 'Sin errores pendientes.' : 'Aún no hay errores resueltos.'}</td></tr>
            ) : errores.map((e) => {
              const sev = SEVERIDAD_COLORS[e.severidad] || SEVERIDAD_COLORS.aviso;
              const abierto = expandido === e.id;
              const u = e.usuario_detalle;
              const wa = u && u.whatsapp ? `https://wa.me/${u.whatsapp.replace(/\D/g, '')}` : null;
              return (
                <React.Fragment key={e.id}>
                  <tr>
                    <td>
                      {vista === 'pendientes' && (
                        <input type="checkbox" checked={seleccionados.has(e.id)} onChange={() => toggle(e.id)} />
                      )}
                    </td>
                    <td className="text-sm text-secondary" style={{ whiteSpace: 'nowrap' }}>{formatFecha(e.ultima_ocurrencia)}</td>
                    <td>
                      <span style={{ background: sev.bg, color: sev.color, borderRadius: '6px', padding: '3px 8px', fontWeight: 600, fontSize: '12px' }}>
                        {sev.label}
                      </span>
                    </td>
                    <td className="text-sm">
                      {u ? (
                        <>
                          <div style={{ fontWeight: 600 }}>{u.nombre}</div>
                          <div className="text-xs text-secondary">
                            {u.casillero || e.rol}
                            {wa && (
                              <> · <a href={wa} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                                <MessageCircle size={11} style={{ verticalAlign: '-1px' }} /> WhatsApp
                              </a></>
                            )}
                          </div>
                        </>
                      ) : '—'}
                    </td>
                    <td className="text-sm mono">{e.metodo} {e.endpoint || '(error de pantalla)'}</td>
                    <td className="text-sm">
                      <strong>{e.status_code === 0 ? 'Sin respuesta' : `HTTP ${e.status_code}`}</strong>
                      <div className="text-xs text-secondary">{explicarStatus(e.status_code)}</div>
                    </td>
                    <td className="mono" style={{ fontWeight: 600 }}>{e.ocurrencias}</td>
                    <td>
                      <button className="btn-ghost btn-sm" onClick={() => setExpandido(abierto ? null : e.id)} aria-label="Ver detalle">
                        {abierto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </td>
                  </tr>
                  {abierto && (
                    <tr>
                      <td colSpan={8} style={{ padding: 'var(--spacing-4)', background: 'var(--color-bg-secondary)' }}>
                        <div className="text-sm" style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                          <div><strong>Lo que vio en pantalla:</strong> {e.mensaje_mostrado || '—'}</div>
                          <div><strong>Primera vez:</strong> {formatFecha(e.primera_ocurrencia)} · <strong>Origen:</strong> {e.origen_display}</div>
                          {u && <div><strong>Correo:</strong> {u.email}</div>}
                          {e.contexto && e.contexto.archivos && e.contexto.archivos.length > 0 && (
                            <div>
                              <strong>Archivos que adjuntó:</strong>{' '}
                              {e.contexto.archivos.map((a) => `${a.nombre} (${a.tipo || 'tipo desconocido'}, ${a.tamano_kb} KB)`).join('; ')}
                            </div>
                          )}
                          <div className="text-xs text-secondary"><strong>Navegador:</strong> {e.user_agent || '—'}</div>
                          {e.detalle && (
                            <pre className="mono text-xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '220px', overflow: 'auto', margin: 0 }}>
                              {e.detalle}
                            </pre>
                          )}
                          <div>
                            <label>Notas (causa / qué hiciste)</label>
                            <textarea
                              rows={2}
                              value={notasTemp[e.id] !== undefined ? notasTemp[e.id] : e.notas}
                              onChange={(ev) => setNotasTemp((t) => ({ ...t, [e.id]: ev.target.value }))}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                            <button className="btn-secondary btn-sm" onClick={() => guardar(e.id, { notas: notasTemp[e.id] !== undefined ? notasTemp[e.id] : e.notas }, 'Notas guardadas.')}>
                              Guardar notas
                            </button>
                            <button className="btn-primary btn-sm" onClick={() => guardar(e.id, { resuelto: !e.resuelto }, e.resuelto ? 'Reabierto.' : 'Marcado como resuelto.')}>
                              <CheckCircle2 size={14} /> {e.resuelto ? 'Reabrir' : 'Marcar resuelto'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
